/**
 * BlockRenderer - Main dispatch component for rendering SIB blocks
 * 
 * This component looks up the appropriate renderer for each block type
 * and handles common block chrome (sources affordance, explain toggle, etc.)
 */

import React, { memo, Suspense, lazy, ComponentType } from 'react'
import { Block, BlockType, getBlockCategory } from '../../../lib/cockpit/schemas'
import { useScenario } from '../scenario/ScenarioContext'

// Common props interface for all block components
interface BlockComponentProps {
  data: unknown
  blockId: string
  title?: string
  compact?: boolean
  showExplain?: boolean
  onNavigate?: (target: { tab?: string; entity_id?: string; section_id?: string }) => void
}

// Lazy load heavy block components
const StatusStripBlock = lazy(() => import('./signals/StatusStripBlock'))
const SignalCardBlock = lazy(() => import('./signals/SignalCardBlock'))
const VerdictBlock = lazy(() => import('./signals/VerdictBlock'))
const ClaimCardBlock = lazy(() => import('./understanding/ClaimCardBlock'))
const CausalChainBlock = lazy(() => import('./understanding/CausalChainBlock'))
const TimelineBlock = lazy(() => import('./understanding/TimelineBlock'))
const IssueTreeBlock = lazy(() => import('./understanding/IssueTreeBlock'))
const GlossaryBlock = lazy(() => import('./understanding/GlossaryBlock'))
const VizFrameBlock = lazy(() => import('./exploration/VizFrameBlock'))
const DataTableBlock = lazy(() => import('./exploration/DataTableBlock'))
const EntityProfileBlock = lazy(() => import('./exploration/EntityProfileBlock'))
const ComparisonGridBlock = lazy(() => import('./exploration/ComparisonGridBlock'))
const DecisionOptionsBlock = lazy(() => import('./action/DecisionOptionsBlock'))
const WatchlistBlock = lazy(() => import('./action/WatchlistBlock'))
const ChecklistBlock = lazy(() => import('./action/ChecklistBlock'))
const EvidenceBundleBlock = lazy(() => import('./trust/EvidenceBundleBlock'))
const AssumptionsBlock = lazy(() => import('./trust/AssumptionsBlock'))
const LimitationsBlock = lazy(() => import('./trust/LimitationsBlock'))
const ChangeLogBlock = lazy(() => import('./trust/ChangeLogBlock'))

// SIB-specific blocks
const ComparisonMatrixBlock = lazy(() => import('./analysis/ComparisonMatrixBlock'))
const RiskMatrixBlock = lazy(() => import('./analysis/RiskMatrixBlock'))
const RatingScorecardBlock = lazy(() => import('./analysis/RatingScorecardBlock'))
const ScenarioComparisonBlock = lazy(() => import('./exploration/ScenarioComparisonBlock'))
const SensitivityAnalysisBlock = lazy(() => import('./analysis/SensitivityAnalysisBlock'))
const GenericSIBBlock = lazy(() => import('./GenericSIBRenderer'))

// Block type to component mapping
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BLOCK_COMPONENTS: Record<BlockType, React.LazyExoticComponent<ComponentType<any>>> = {
  status_strip: StatusStripBlock,
  signal_card: SignalCardBlock,
  verdict: VerdictBlock,
  claim_card: ClaimCardBlock,
  causal_chain: CausalChainBlock,
  timeline: TimelineBlock,
  issue_tree: IssueTreeBlock,
  glossary: GlossaryBlock,
  viz_frame: VizFrameBlock,
  data_table: DataTableBlock,
  entity_profile: EntityProfileBlock,
  comparison_grid: ComparisonGridBlock,
  decision_options: DecisionOptionsBlock,
  watchlist: WatchlistBlock,
  checklist: ChecklistBlock,
  evidence_bundle: EvidenceBundleBlock,
  assumptions: AssumptionsBlock,
  limitations: LimitationsBlock,
  change_log: ChangeLogBlock,
  jump_chips: lazy(() => import('./utility/JumpChipsBlock')),
  section_header: lazy(() => import('./utility/SectionHeaderBlock')),
  // SIB-specific blocks
  comparison_matrix: ComparisonMatrixBlock,
  risk_matrix: RiskMatrixBlock,
  rating_scorecard: RatingScorecardBlock,
  scenario_comparison: ScenarioComparisonBlock,
  sensitivity_analysis: SensitivityAnalysisBlock,
  // Generic SIB for flexible content (SWOT, PESTEL, quotes, etc.)
  generic_sib: GenericSIBBlock,
}

// Category colors for block chrome
const CATEGORY_COLORS = {
  signals: 'border-blue-500/20 bg-blue-500/5',
  understanding: 'border-purple-500/20 bg-purple-500/5',
  exploration: 'border-green-500/20 bg-green-500/5',
  action: 'border-orange-500/20 bg-orange-500/5',
  trust: 'border-gray-500/20 bg-gray-500/5',
  utility: 'border-transparent bg-transparent',
  analysis: 'border-amber-500/20 bg-amber-500/5',
} as const

export interface BlockRendererProps {
  block: Block
  onOpenEvidence?: (blockId: string) => void
  onExplain?: (blockId: string) => void
  className?: string
  compact?: boolean
  showChrome?: boolean
}

function BlockRendererInner({
  block,
  onOpenEvidence,
  onExplain,
  className = '',
  compact = false,
  showChrome = true,
}: BlockRendererProps) {
  const { activeState, getScenarioValue } = useScenario()
  const [isCollapsed, setIsCollapsed] = React.useState(block.collapsed ?? false)
  const [showExplain, setShowExplain] = React.useState(false)

  const category = getBlockCategory(block.type)
  const categoryColor = category ? CATEGORY_COLORS[category] : CATEGORY_COLORS.utility
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const BlockComponent = BLOCK_COMPONENTS[block.type] as React.LazyExoticComponent<ComponentType<any>> | undefined

  // Apply scenario bindings if present
  const scenarioAwareData = React.useMemo(() => {
    if (!block.scenario_binding?.by_state || !activeState) {
      return (block as any).data
    }
    
    const stateValue = block.scenario_binding.by_state[activeState]
    if (stateValue !== undefined) {
      // Merge scenario value into block data
      return {
        ...(block as any).data,
        _scenario_value: stateValue,
      }
    }
    return (block as any).data
  }, [block, activeState])
  
  if (!BlockComponent) {
    console.warn(`Unknown block type: ${block.type}`)
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-red-600 dark:text-red-400 text-sm">
          Unknown block type: {block.type}
        </p>
      </div>
    )
  }

  // Don't show chrome for utility blocks
  if (!showChrome || category === 'utility') {
    return (
      <Suspense fallback={<BlockSkeleton />}>
        <BlockComponent
          data={scenarioAwareData}
          blockId={block.id}
          title={block.title}
          compact={compact}
        />
      </Suspense>
    )
  }

  return (
    <div
      className={`
        rounded-xl border transition-all duration-200
        ${categoryColor}
        ${isCollapsed ? 'py-2' : 'py-4'}
        ${className}
      `}
    >
      {/* Block Header (collapsible) */}
      {block.title && (
        <div
          className={`
            flex items-center justify-between px-4 cursor-pointer
            ${isCollapsed ? '' : 'pb-3 border-b border-gray-200/50 dark:border-gray-700/50'}
          `}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div className="flex items-center gap-2">
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {block.title}
            </h3>
          </div>
          
          {/* Block Actions */}
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            {onOpenEvidence && (
              <button
                onClick={() => onOpenEvidence(block.id)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="View sources"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setShowExplain(!showExplain)}
              className={`
                p-1.5 rounded-lg transition-colors
                ${showExplain 
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500'
                }
              `}
              title="Explain"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Block Content */}
      {!isCollapsed && (
        <div className={block.title ? 'px-4 pt-3' : 'px-4'}>
          <Suspense fallback={<BlockSkeleton />}>
            <BlockComponent
              data={scenarioAwareData}
              blockId={block.id}
              title={block.title}
              compact={compact}
              showExplain={showExplain}
            />
          </Suspense>
        </div>
      )}
    </div>
  )
}

// Memoize to prevent unnecessary re-renders
export const BlockRenderer = memo(BlockRendererInner)

// Loading skeleton for blocks
function BlockSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  )
}

// Render a list of blocks with proper spacing
export interface BlockListProps {
  blocks: Block[]
  onOpenEvidence?: (blockId: string) => void
  className?: string
  gap?: 'sm' | 'md' | 'lg'
}

export function BlockList({ blocks, onOpenEvidence, className = '', gap = 'md' }: BlockListProps) {
  const gapClass = {
    sm: 'space-y-2',
    md: 'space-y-4',
    lg: 'space-y-6',
  }[gap]

  return (
    <div className={`${gapClass} ${className}`}>
      {blocks.map(block => (
        <BlockRenderer
          key={block.id}
          block={block}
          onOpenEvidence={onOpenEvidence}
        />
      ))}
    </div>
  )
}

export default BlockRenderer



